// Nombres para mostrar de las personas de Notion. Sin la capacidad "leer información de
// usuarios", Notion no entrega nombre ni correo de algunas personas (p. ej. quienes ya no están
// en el workspace). Se usa, en orden: el nombre de Notion, el de la cuenta de Tablero vinculada
// a esa persona y, si no hay ninguno, "Sin nombre ·a1b2" (final del id, para distinguirlas).

import { initials } from './format.ts';
import type { LinkCandidate, NotionUser, PersonSummary } from './types.ts';

export interface PersonInfo {
  id: string;
  label: string;
  /** false si ni Notion ni una cuenta vinculada dan un nombre. */
  named: boolean;
  /** Iniciales del nombre, o "?" sin nombre. */
  initials: string;
  /** Tono estable (0–359) para el color del avatar. */
  hue: number;
}

export function shortId(id: string): string {
  return id.replace(/-/g, '').slice(-4).toLowerCase();
}

export function personHue(id: string): number {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

function profileName(profile: PersonSummary | undefined): string | null {
  if (!profile) return null;
  return profile.display_name?.trim() || profile.email.split('@')[0] || null;
}

export function describePerson(
  id: string,
  user?: Pick<NotionUser, 'name'> | null,
  profile?: PersonSummary,
): PersonInfo {
  const name = user?.name?.trim() || profileName(profile);
  return {
    id,
    label: name ?? `Sin nombre ·${shortId(id)}`,
    named: name !== null,
    initials: name ? initials(name) : '?',
    hue: personHue(id),
  };
}

export type PeopleIndex = ReadonlyMap<string, PersonInfo>;

export function peopleIndex(
  notionUsers: readonly NotionUser[],
  profiles: readonly PersonSummary[],
): PeopleIndex {
  const byNotion = new Map(
    profiles.flatMap((p) => (p.notion_user_id ? [[p.notion_user_id, p]] : [])),
  );
  return new Map(notionUsers.map((u) => [u.id, describePerson(u.id, u, byNotion.get(u.id))]));
}

/** Persona del índice; un id que aún no se sincronizó se muestra sin nombre. */
export function personInfo(index: PeopleIndex, id: string): PersonInfo {
  return index.get(id) ?? describePerson(id);
}

/** Con nombre primero (alfabético), luego sin nombre. */
export function comparePeople(a: PersonInfo, b: PersonInfo): number {
  if (a.named !== b.named) return a.named ? -1 : 1;
  return a.label.localeCompare(b.label, 'es');
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Contexto para reconocerse en el vínculo: "12 tarjetas (2 abiertas): Rediseño home, …". */
export function cardsSummary(c: LinkCandidate): string {
  if (c.total_cards === 0) return 'No figura en tarjetas de Tablero';
  const open = c.open_cards === 0 ? 'todas cerradas' : plural(c.open_cards, 'abierta', 'abiertas');
  const sample = c.sample_cards.join(', ') + (c.total_cards > c.sample_cards.length ? '…' : '');
  return `${plural(c.total_cards, 'tarjeta', 'tarjetas')} (${open}): ${sample}`;
}
